import { useCallback, useState } from 'react';

import { isValidEmail, type Role } from '../../shared/contract';
import { subscribe } from '../lib/api';

/**
 * - `idle`       - nothing submitted yet.
 * - `submitting` - request in flight; the button is disabled.
 * - `pending`    - accepted; waiting on the visitor's opt-in click.
 * - `error`      - `message` is safe to show as-is.
 */
export type SignupState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'pending'; email: string; role: Role }
  | { status: 'error'; message: string };

export interface UseWaitlistSignup {
  state: SignupState;
  submit: (input: { email: string; role: Role; honeypot: string }) => Promise<void>;
  /** Clears an error so the visitor sees a clean form as soon as they retype. */
  reset: () => void;
  /**
   * Re-submits the pending sign-up. Resolves true if the provider accepted it.
   *
   * Server-side this deletes and recreates a *pending* contact, which is what
   * makes the provider send a fresh confirmation. A contact that is already
   * confirmed, or has unsubscribed, is left untouched - so a true result means
   * the request was handled, not that an email was necessarily sent.
   */
  resend: () => Promise<boolean>;
}

export function useWaitlistSignup(): UseWaitlistSignup {
  const [state, setState] = useState<SignupState>({ status: 'idle' });

  const reset = useCallback(() => {
    setState((current) => (current.status === 'error' ? { status: 'idle' } : current));
  }, []);

  const submit = useCallback<UseWaitlistSignup['submit']>(async ({ email, role, honeypot }) => {
    const trimmed = email.trim();

    // Client-side check is a courtesy only - the server validates again, since
    // anything reaching a public endpoint is untrusted by definition.
    if (!isValidEmail(trimmed)) {
      setState({ status: 'error', message: 'Enter a valid email address.' });
      return;
    }

    setState({ status: 'submitting' });
    const response = await subscribe({ email: trimmed, role, honeypot });

    switch (response.status) {
      case 'pending':
        setState({ status: 'pending', email: trimmed, role });
        return;

      case 'error':
        setState({ status: 'error', message: response.message });
        return;
    }
  }, []);

  const resend = useCallback(async (): Promise<boolean> => {
    if (state.status !== 'pending') return false;
    const response = await subscribe({
      email: state.email,
      role: state.role,
      honeypot: '',
      intent: 'resend',
    });
    return response.status === 'pending';
  }, [state]);

  return { state, submit, reset, resend };
}
